import type { ExpenseCatalog, ListExpenseCatalogsQuery } from '@clockwise/clients/expense-catalogs';
import { ApiError, EXPENSE_PERIOD_LABELS, EXPENSE_PERIODS } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Filter,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { formatCostOrVariable } from '@/components/custom/expense-catalog-form';
import { ExpenseCatalogFormSheet } from '@/components/custom/expense-catalog-form-sheet';
import { ExpenseCatalogImportSheet } from '@/components/custom/expense-catalog-import-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { expenseCatalogsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['name', 'cost', 'period', 'createdAt', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  period: z.enum(EXPENSE_PERIODS).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/expense-catalogs/')({
  validateSearch: listSearch,
  component: ExpenseCatalogsPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';
const ALL_PERIODS = 'all';

/**
 * Fixed column widths, keyed by column id.
 *
 * The table is `table-layout: fixed`, so the header row alone decides the
 * widths — without this the browser sizes each column to its content, and the
 * layout visibly shifts three times on a single page: skeleton → loaded, then
 * again on every page change or filter whose longest name/description differs.
 *
 * `description` is deliberately absent: under a fixed layout the unsized
 * column absorbs whatever space is left, so the flexible column is the one
 * with the most variable content.
 */
const COLUMN_WIDTHS: Record<string, string> = {
  name: 'w-[22%]',
  cost: 'w-28',
  period: 'w-28',
  updatedAt: 'w-28',
  actions: 'w-24',
};

function ExpenseCatalogsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const period = search.period;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // One sheet serves both add and edit — `editing` null means create.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCatalog | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  // Export mirrors the on-screen filters exactly — what the user sees is what
  // they get in the file. Reads `searchInput` (the typed value), NOT URL-state
  // `q`, because the search box debounces by 300ms before committing to the
  // URL. Clicking Export within that window would otherwise export the
  // previous query instead of what's currently typed.
  const exportMutation = useMutation({
    mutationFn: () =>
      expenseCatalogsApi.exportXlsx({
        active: status === 'all' ? undefined : status === 'active',
        period,
        q: searchInput.trim() || undefined,
        sortField,
        sortDirection,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'expense-catalogs.xlsx');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Export failed';
      toast.error(msg);
    },
  });

  const listQuery: ListExpenseCatalogsQuery = {
    q: q.trim() || undefined,
    active: status === 'all' ? undefined : status === 'active',
    period,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.expenseCatalogs.list(listQuery),
    queryFn: () => expenseCatalogsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.expenseCatalogs.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => expenseCatalogsApi.remove(id),
    onSuccess: () => {
      toast.success('Expense item deactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Deactivate failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => expenseCatalogsApi.restore(id),
    onSuccess: () => {
      toast.success('Expense item reactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<ExpenseCatalog>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        // The name is truncated rather than wrapped: the column is a fixed
        // width, so a long name would otherwise reflow the row height and
        // reintroduce the jitter the fixed layout exists to remove. The
        // "(inactive)" marker sits outside the link and never shrinks, so it
        // stays readable however long the name is.
        cell: ({ row }) => (
          <div className="flex items-baseline gap-2">
            <Link
              to="/setup/expense-catalogs/$id"
              params={{ id: row.original.id }}
              title={row.original.name}
              className="truncate font-medium text-primary underline-offset-2 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.name}
            </Link>
            {!row.original.active ? (
              <span className="shrink-0 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) => (
          <span
            className="line-clamp-1 text-sm text-muted-foreground"
            title={row.original.description}
          >
            {row.original.description}
          </span>
        ),
      },
      {
        accessorKey: 'cost',
        header: 'Cost',
        // A variable item's cell says "Variable" in muted text rather than
        // showing an amount. `num` (tabular figures, right-aligned) is dropped
        // for that case — the word is not a number and lining it up with the
        // decimal points of the rows around it just looks broken.
        cell: ({ row }) =>
          row.original.variable ? (
            <span className="text-sm text-muted-foreground">
              {formatCostOrVariable(row.original.cost)}
            </span>
          ) : (
            <span className="text-sm tabular-nums">{formatCostOrVariable(row.original.cost)}</span>
          ),
      },
      {
        accessorKey: 'period',
        header: 'Period',
        cell: ({ row }) => (
          <span className="text-sm">
            {EXPENSE_PERIOD_LABELS[row.original.period] ?? row.original.period}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="expensecatalog.edit">
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row.original);
                  setSheetOpen(true);
                }}
              >
                <Pencil />
              </Button>
              {row.original.active ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Deactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Deactivate "${row.original.name}"?`)) {
                      deleteMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Reactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="Expense catalog"
        subtitle="Pass-through items you can bill on to a customer."
        count={total}
      >
        <RequirePermission permission="expensecatalog.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="expensecatalog.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="expensecatalog.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add expense item
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search name, description…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={period ?? ALL_PERIODS}
          onValueChange={(v) =>
            updateSearch({
              period: v === ALL_PERIODS ? undefined : (v as ListSearch['period']),
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PERIODS}>All periods</SelectItem>
            {EXPENSE_PERIODS.map((p) => (
              <SelectItem key={p} value={p}>
                {EXPENSE_PERIOD_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        {/* `table-fixed` pins the columns to COLUMN_WIDTHS below. With the
            default auto layout the browser re-measures on every data change,
            so the columns jump between the skeleton, the loaded page, and each
            subsequent page. */}
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id} className={COLUMN_WIDTHS[h.column.id]}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load expense catalog.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q || period || status !== 'active'
                    ? 'No expense items match these filters.'
                    : 'No expense items yet. Click “Add expense item”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/setup/expense-catalogs/$id',
                      params: { id: r.original.id },
                    })
                  }
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <ExpenseCatalogFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        item={editing}
      />
      <ExpenseCatalogImportSheet open={importOpen} onOpenChange={setImportOpen} />
    </PageShell>
  );
}

const defaultCell: ColumnDef<ExpenseCatalog>['cell'] = ({ getValue }) => String(getValue() ?? '');

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.period) out.period = s.period;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  return out;
}
